import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Calendar, ClipboardList, Package, Palette, ShoppingBag } from 'lucide-react';
import { Spinner } from '../components/Spinner';
import styles from './DashboardPage.module.css';

interface Stats {
  workshops: number;
  reservations: { total: number; pending: number };
  customOrders: { total: number; pending: number };
  paintKits: number;
  paintKitOrders: { total: number; pending: number };
}

export function DashboardPage() {
  const { admin, token } = useAuth();
  const [stats, setStats] = useState<Stats>({
    workshops: 0,
    reservations: { total: 0, pending: 0 },
    customOrders: { total: 0, pending: 0 },
    paintKits: 0,
    paintKitOrders: { total: 0, pending: 0 },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [workshopsRes, reservationsRes, customOrdersRes, paintKitsRes, paintKitOrdersRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/workshops`, { headers }),
          fetch(`${API_BASE_URL}/api/reservations`, { headers }),
          fetch(`${API_BASE_URL}/api/custom-orders`, { headers }),
          fetch(`${API_BASE_URL}/api/painting-kits`, { headers }),
          fetch(`${API_BASE_URL}/api/painting-kit-orders`, { headers }),
        ]);

        const workshopsData = await workshopsRes.json();
        const reservationsData = await reservationsRes.json();
        const customOrdersData = await customOrdersRes.json();
        const paintKitsData = await paintKitsRes.json();
        const paintKitOrdersData = await paintKitOrdersRes.json();

        const reservations = reservationsData.reservations || [];
        const customOrders = customOrdersData.orders || [];
        const paintKitOrders = paintKitOrdersData.orders || [];

        setStats({
          workshops: workshopsData.workshops?.length || 0,
          reservations: {
            total: reservations.length,
            pending: reservations.filter((r: any) => r.status === 'pending').length,
          },
          customOrders: {
            total: customOrders.length,
            pending: customOrders.filter((o: any) => o.status === 'pending').length,
          },
          paintKits: paintKitsData.kits?.length || 0,
          paintKitOrders: {
            total: paintKitOrders.length,
            pending: paintKitOrders.filter((o: any) => o.status === 'pending').length,
          },
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  const statCards = [
    {
      icon: Calendar,
      label: 'Active Workshops',
      value: stats.workshops,
      color: '#8b5cf6',
    },
    {
      icon: ClipboardList,
      label: 'Reservations',
      value: stats.reservations.total,
      subtext: `${stats.reservations.pending} pending`,
      color: '#10b981',
    },
    {
      icon: Package,
      label: 'Custom Orders',
      value: stats.customOrders.total,
      subtext: `${stats.customOrders.pending} pending`,
      color: '#f59e0b',
    },
    {
      icon: ShoppingBag,
      label: 'Kit Orders',
      value: stats.paintKitOrders.total,
      subtext: `${stats.paintKitOrders.pending} pending`,
      color: '#06b6d4',
    },
    {
      icon: Palette,
      label: 'Paint Kits',
      value: stats.paintKits,
      color: '#ec4899',
    },
  ];

  return (
    <div className={styles.dashboard}>
      <header className={styles.header}>
        <div>
          <h1>Welcome back, {admin?.name || 'Admin'}!</h1>
          <p>Here's what's happening with your business today.</p>
        </div>
      </header>

      {loading ? (
        <div className={styles.loading}>Loading dashboard...</div>
      ) : (
        <>
          <div className={styles.statsGrid}>
            {statCards.map((card, index) => (
              <div key={index} className={styles.statCard}>
                <div
                  className={styles.statIcon}
                  style={{ backgroundColor: `${card.color}20`, color: card.color }}
                >
                  <card.icon size={24} />
                </div>
                <div className={styles.statContent}>
                  <span className={styles.statValue}>{card.value}</span>
                  <span className={styles.statLabel}>{card.label}</span>
                  {card.subtext && (
                    <span className={styles.statSubtext}>{card.subtext}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.quickActions}>
            <h2>Quick Actions</h2>
            <div className={styles.actionsGrid}>
              <a href="/workshops" className={styles.actionCard}>
                <Calendar size={20} />
                <span>Manage Workshops</span>
              </a>
              <a href="/reservations" className={styles.actionCard}>
                <ClipboardList size={20} />
                <span>View Reservations</span>
              </a>
              <a href="/custom-orders" className={styles.actionCard}>
                <Package size={20} />
                <span>Custom Orders</span>
              </a>
              <a href="/paint-kit-orders" className={styles.actionCard}>
                <ShoppingBag size={20} />
                <span>Kit Orders</span>
              </a>
              <a href="/paint-kits" className={styles.actionCard}>
                <Palette size={20} />
                <span>Manage Products</span>
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
